import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, RefreshCcw, ScrollText, CheckCircle2, XCircle, Edit3, ExternalLink,
} from "lucide-react";

const STATUS_CLS = {
  GRANTED:            { c: "bg-emerald-100 text-emerald-800 border-emerald-300", l: "Deferido" },
  DENIED:             { c: "bg-rose-100 text-rose-800 border-rose-300",           l: "Indeferido" },
  PARTIALLY_GRANTED:  { c: "bg-yellow-100 text-yellow-800 border-yellow-300",     l: "Parcialmente deferido" },
  MODIFIED:           { c: "bg-blue-100 text-blue-800 border-blue-300",           l: "Modificado" },
  EXCLUDED:           { c: "bg-slate-200 text-slate-700 border-slate-300",        l: "Excluído" },
  UNKNOWN:            { c: "bg-slate-100 text-slate-600 border-slate-300",        l: "Indefinido" },
  REVIEW_REQUIRED:    { c: "bg-amber-100 text-amber-800 border-amber-300",        l: "Revisão necessária" },
};

const DECISION_LABEL = {
  SENTENCE: "Sentença", SENTENCE_ED: "ED da sentença",
  TRT_APPEAL: "Recurso ordinário", TRT_JUDGMENT: "Acórdão TRT", TRT_ED: "ED do TRT",
  TST_DECISION: "Decisão TST", TST_JUDGMENT: "Acórdão TST", TST_ED: "ED do TST",
  EXECUTION_DECISION: "Decisão de execução", SETTLEMENT: "Acordo",
};

export default function LiquidacaoTitulo() {
  const { id } = useParams();
  const [title, setTitle] = useState(null);
  const [versions, setVersions] = useState([]);
  const [building, setBuilding] = useState(false);

  const load = useCallback(async () => {
    const [t, v] = await Promise.all([
      api.get(`/liq/cases/${id}/title`),
      api.get(`/liq/cases/${id}/title/versions`),
    ]);
    setTitle(t.data.exists ? t.data.title : null);
    setVersions(v.data.rows || []);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const build = async () => {
    setBuilding(true);
    try {
      const r = await api.post(`/liq/cases/${id}/title/build`);
      if (r.data.ok) {
        toast.success(`Título consolidado — versão ${r.data.version} · ${r.data.chapters.length} capítulos.`);
        await load();
      } else {
        toast.error(r.data.message || "Não foi possível consolidar.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha na consolidação");
    } finally { setBuilding(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-6 px-6" data-testid="liq-titulo">
      <div className="max-w-6xl mx-auto space-y-4">
        <Link to={`/calculos/liquidacao-trabalhista/${id}`}
          className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
          data-testid="liq-titulo-back">
          <ArrowLeft className="w-3.5 h-3.5"/> Voltar ao caso
        </Link>

        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500">
              Hermes Calc · Título Executivo
            </div>
            <h1 className="font-heading text-3xl font-semibold text-slate-900">
              Consolidação do título
            </h1>
            <p className="mt-1 text-sm text-slate-600 max-w-2xl">
              O Hermes lê as decisões (sentença → embargos → acórdão → TST) e
              consolida capítulo por capítulo. Decisão recursal posterior <b>prevalece
              apenas nos pontos que efetivamente julgou</b>; capítulos não atingidos
              permanecem intactos.
            </p>
          </div>
          <button onClick={build} disabled={building}
            data-testid="liq-titulo-build"
            className="h-11 px-4 rounded-xl bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white text-sm font-medium inline-flex items-center gap-2">
            <RefreshCcw className={`w-4 h-4 ${building ? "animate-spin" : ""}`}/>
            {building ? "Consolidando…" : title ? "Consolidar novamente" : "Consolidar título"}
          </button>
        </div>

        {/* Timeline de fontes */}
        {title && title.source_documents?.length > 0 && (
          <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="liq-titulo-sources">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">
              Documentos consumidos · versão {title.version}
            </div>
            <div className="flex flex-wrap gap-2">
              {title.source_documents.map((d, i) => (
                <span key={i} className="text-xs px-2 py-1 rounded-md bg-slate-100 text-slate-700">
                  {DECISION_LABEL[d.category] || d.category}
                  {d.filename && <span className="text-slate-400 ml-1">· {d.filename}</span>}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Chapters */}
        {!title ? (
          <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-white p-10 text-center"
               data-testid="liq-titulo-empty">
            <ScrollText className="w-10 h-10 text-slate-300 mx-auto mb-3"/>
            <div className="text-slate-700 font-medium">
              Título ainda não consolidado.
            </div>
            <div className="text-sm text-slate-500 mt-1">
              Envie a sentença e (se houver) acórdãos, deixe o Hermes classificar
              os documentos e clique em <b>Consolidar título</b>.
            </div>
          </div>
        ) : (
          <div className="space-y-2" data-testid="liq-titulo-chapters">
            {title.chapters.map((ch, i) => (
              <ChapterCard key={i} ch={ch}/>
            ))}
          </div>
        )}

        {/* Versões */}
        {versions.length > 1 && (
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">
              Histórico de versões ({versions.length})
            </div>
            <div className="space-y-1 text-xs">
              {versions.map(v => (
                <div key={v.id} className="flex items-center gap-2 text-slate-600">
                  <b>v{v.version}</b>
                  <span>·</span>
                  <span>{new Date(v.generated_at).toLocaleString("pt-BR")}</span>
                  <span>·</span>
                  <span>{v.source_documents?.length || 0} doc(s)</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ChapterCard({ ch }) {
  const st = STATUS_CLS[ch.status] || STATUS_CLS.UNKNOWN;
  const superseded = ch.superseded_decision;
  return (
    <div className="rounded-xl border-l-4 border-slate-300 border border-slate-200 bg-white p-4"
         data-testid={`liq-titulo-chapter-${ch.code}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-semibold text-slate-900">{ch.name || ch.code}</div>
            <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase font-semibold border ${st.c}`}>
              {st.l}
            </span>
            <span className="text-[10px] text-slate-500">
              confiança {Math.round((ch.confidence || 0)*100)}%
            </span>
          </div>
          <div className="mt-1 text-xs font-mono text-slate-400">{ch.code}</div>
          {ch.period && (
            <div className="mt-1 text-xs text-slate-600"><b>Período:</b> {ch.period}</div>
          )}
          {ch.parameters && Object.keys(ch.parameters).length > 0 && (
            <div className="mt-2 text-xs">
              <b className="text-slate-700">Parâmetros:</b>
              <ul className="mt-1 pl-4 list-disc text-slate-600">
                {Object.entries(ch.parameters).map(([k, v]) => (
                  <li key={k}><span className="font-mono">{k}</span> = {typeof v === "boolean" ? (v ? "Sim" : "Não") : String(v)}</li>
                ))}
              </ul>
            </div>
          )}
          {ch.excerpt && (
            <div className="mt-2 text-xs italic text-slate-700 bg-slate-50 rounded p-2">
              "{ch.excerpt}"
            </div>
          )}
          {ch.legal_notes && (
            <div className="mt-2 text-xs text-slate-600">
              <b>Notas jurídicas:</b> {ch.legal_notes}
            </div>
          )}
          <div className="mt-2 flex items-center gap-2 text-[11px] text-slate-500">
            <span>Fonte: <b>{DECISION_LABEL[ch.source_decision] || ch.source_decision}</b></span>
            {superseded && (
              <>
                <span>·</span>
                <span>substitui: <b className="line-through">{DECISION_LABEL[superseded] || superseded}</b></span>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
